import {loadPosterAsHistoryBaseline} from '@PosterWhiteboard/poster/poster-init';
import {loadOriginalImageIntoEditorOnLayersFailure} from '@PosterWhiteboard/poster/add-ai-chat-image-to-editor';
import {openMakeTextEditableLoadingModal} from '@Modals/make-text-editable-loading-modal';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {setPendingDesignBackdropUrl, setPosterOverlayLoaderVisibility} from '@Components/poster-editor/poster-editor-reducer';
import {clearModalHistoryEntry} from '@Components/modal-container/modal-history-middleware';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {replacePosterLoadUrl} from '@Libraries/poster-editor.library';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PendingDesignRevealConfig} from './pending-design-reveal.library.types';
import {
  PENDING_DESIGN_ERROR_GROWL_KEY,
  PENDING_DESIGN_READY_GROWL_INTERVAL_MS,
  PENDING_DESIGN_READY_GROWL_KEY,
  PendingDesignPollStatus,
} from './pending-design-reveal.library.types';

/** Editor-tab reveal for a blank poster pre-created for a running job; saves are held so the placeholder never overwrites the result. */
export const runPendingDesignReveal = async (config: PendingDesignRevealConfig): Promise<void> => {
  showPendingDesignLoading(config);
  const poster = window.posterEditor?.whiteboard;
  poster?.savePoster.pauseSaving();

  let revealedPosterHashedId: string | null = null;
  let isHeldForStillRunningJob = false;
  try {
    const outcome = await config.pollJob(config.jobId);
    if (outcome.status === PendingDesignPollStatus.TIMED_OUT) {
      holdForStillRunningJob();
      isHeldForStillRunningJob = true;
      return;
    }
    if (!isOpenPoster(poster, outcome.posterHashedId)) {
      window.location.assign(config.getPosterUrlWithFeedback(outcome.posterHashedId));
      return;
    }
    await loadPosterAsHistoryBaseline(outcome.posterHashedId);
    poster.savePoster.resumeSaving();
    revealedPosterHashedId = outcome.posterHashedId;
  } catch (e: unknown) {
    console.error(e);
    await showPendingDesignFailure(config, poster);
  } finally {
    if (!isHeldForStillRunningJob) {
      hidePendingDesignLoading();
    }
  }

  if (revealedPosterHashedId !== null) {
    announceRevealedDesign(config, revealedPosterHashedId);
  }
};

const showPendingDesignLoading = ({originalImageUrl, loadingCopy}: PendingDesignRevealConfig): void => {
  window.PMW.redux.store.dispatch(setPendingDesignBackdropUrl(originalImageUrl));
  window.PMW.redux.store.dispatch(setPosterOverlayLoaderVisibility(true));
  openMakeTextEditableLoadingModal(loadingCopy);
};

const hidePendingDesignLoading = (): void => {
  window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.MAKE_TEXT_EDITABLE_LOADING_MODAL));
  window.PMW.redux.store.dispatch(setPosterOverlayLoaderVisibility(false));
  window.PMW.redux.store.dispatch(setPendingDesignBackdropUrl(null));
};

/** Revealing a different poster here would save it under the open poster's id. */
const isOpenPoster = (poster: Poster | undefined, posterHashedId: string): poster is Poster => {
  return poster?.hashedID === posterHashedId;
};

/** Saving resumes so the fallback image is kept; the URL is rewritten so a refresh does not re-run the failed job. */
const showPendingDesignFailure = async ({originalImageUrl, failureGrowlText}: PendingDesignRevealConfig, poster: Poster | undefined): Promise<void> => {
  poster?.savePoster.resumeSaving();
  rewriteUrlToOpenPoster(poster);
  if (originalImageUrl) {
    await loadOriginalImageIntoEditorOnLayersFailure(originalImageUrl);
  }
  showMessageGrowl({
    key: PENDING_DESIGN_ERROR_GROWL_KEY,
    text: failureGrowlText,
    type: GROWL_TYPE.DANGER,
    showCloseIcon: true,
    sticky: true,
  });
};

const rewriteUrlToOpenPoster = (poster: Poster | undefined): void => {
  if (poster?.hashedID) {
    replacePosterLoadUrl(poster.hashedID);
  }
};

/** Everything stays blocked and saving stays held: the job may still write into this poster, so only a reload can pick it up. */
const holdForStillRunningJob = (): void => {
  openMakeTextEditableLoadingModal({
    title: window.i18next.t('pmwjs_pending_design_still_working_title'),
    texts: [window.i18next.t('pmwjs_pending_design_still_working_text')],
    action: {label: window.i18next.t('pmwjs_reload'), onClick: reloadPendingDesignPage},
  });
};

/** Clears the modal history entry so its queued history.back() cannot cancel the reload. */
const reloadPendingDesignPage = (): void => {
  clearModalHistoryEntry();
  window.location.reload();
};

/** The URL is rewritten so a refresh opens the finished design instead of polling again. */
const announceRevealedDesign = ({readyGrowlText, showFeedback}: PendingDesignRevealConfig, posterHashedId: string): void => {
  showMessageGrowl({
    key: PENDING_DESIGN_READY_GROWL_KEY,
    type: GROWL_TYPE.NEUTRAL,
    icon: 'icon-prints-stack',
    text: readyGrowlText,
    interval: PENDING_DESIGN_READY_GROWL_INTERVAL_MS,
    showCloseIcon: true,
  });
  showFeedback(posterHashedId);
  replacePosterLoadUrl(posterHashedId);
};
